import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { FieldModel, ITespStepInputFieldModel } from '../../../../models/blueprint/blueprint-field.model';
import { IMappedEntityModel } from '../../../../models/project/mapped-entity.model';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { JiraHelper } from '../../../../services/jira.service';
import { ClassicETAPIService } from '../../../../services/api/classic-et.api.service';
import { TEST_STEP_DESCRIPTION_FIELD } from '../../../catch-core/data/constants/blueprint.constants';
import { AttachmentConstants } from '../../../catch-core/data/constants/attachment.constants';
import { RichTextFieldComponent } from '../rich-text-field/rich-text-field.component';
import { ITestStepInstanceModel } from '../../../../models/test-step/test-step-instance.model';

@Component({
  selector: 'app-edit-panel-details-pane',
  templateUrl: './edit-panel-details-pane.component.html',
  styleUrls: ['./edit-panel-details-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class EditPanelDetailsPaneComponent {
  @Input() detailFieldList: FieldModel[] = [];
  @Input() bugFieldList: FieldModel[] = [];
  @Input() testInputFieldList: ITespStepInputFieldModel[] = [];
  @Input() selectedTestStep: ITestStepInstanceModel | null = null;
  @Input() formData: { [fieldId: string]: any } = {};
  @Input() isTestNode = false;
  @Input() projectId = '';
  @Input() workspaceId = '';
  @Input() nodeInstanceId = '';
  // The selected node's own key (e.g. "SJDE-42") - not derivable from selectedTestStep/
  // nodeInstanceId alone since the key lives on the parent node, not the step.
  @Input() nodeKey = '';

  // Guards both halves: `nodeKey + '_Step' + selectedTestStep?.id` inline produced the literal
  // string "..._Stepundefined" whenever nodeKey was set but selectedTestStep was momentarily
  // null (isTestStepSelected can flip true a tick before selectedTestStep itself is populated).
  public get stepFieldImageNamePrefix(): string {
    if (!this.nodeKey || this.selectedTestStep?.id == null) {
      return '';
    }
    return `${this.nodeKey}${AttachmentConstants.STEP_NAME_INFIX}${this.selectedTestStep.id}`;
  }

  @Input() isTestStepSelected = false;
  /** Backlog roll-up: the selected step belongs to another label, so its fields cannot be changed here. */
  @Input() stepFieldsReadOnly = false;
  @Input() isDoView = false;
  @Input() localNodeField: FieldModel | null = null;
  @Input() univeralNodeField: FieldModel | null = null;
  @Input() targetMapsLocalSelected: IMappedEntityModel[] = [];
  @Input() targetMapsUnivlSelected: IMappedEntityModel[] = [];
  @Input() filteredTargetMapsLocal: IMappedEntityModel[] = [];
  @Input() filteredTargetMapsUnivl: IMappedEntityModel[] = [];
  @Input() targetMapsMergedSelected: IMappedEntityModel[] = [];
  @Input() filteredTargetMapsMerged: IMappedEntityModel[] = [];
  @Input() epicNameFieldName = '';
  @Input() startDateFieldName = '';
  @Input() dueDateFieldName = '';
  @Input() extAppReferenceFieldName = '';
  @Input() showEpicNameField = false;
  @Input() buildDropdownOptions: (options: any[]) => { label: string; value: any }[] = () => [];
  @Input() buildUserDropdownOptions: (users: any[]) => { label: string; value: string; disabled?: boolean }[] = () => [];

  @Output() assigneeChange = new EventEmitter<{ index: number; userId: string; fieldId: string; fieldName: string }>();
  @Output() calendarDateChange = new EventEmitter<{ index: number; value: string | null; fieldId: string }>();
  @Output() calendarDateClear = new EventEmitter<{ index: number; fieldId: string }>();
  @Output() calendarDateSet = new EventEmitter<void>();
  @Output() dropdownChange = new EventEmitter<{ value: any; fieldName: string }>();
  @Output() filterTargetMapsLocal = new EventEmitter<AutoCompleteCompleteEvent>();
  @Output() filterTargetMapsUnivl = new EventEmitter<AutoCompleteCompleteEvent>();
  @Output() targetMapLocalSelect = new EventEmitter<IMappedEntityModel>();
  @Output() targetMapLocalUnselect = new EventEmitter<IMappedEntityModel>();
  @Output() targetMapsLocalClear = new EventEmitter<void>();
  @Output() targetMapUnivlSelect = new EventEmitter<IMappedEntityModel>();
  @Output() targetMapUnivlUnselect = new EventEmitter<IMappedEntityModel>();
  @Output() targetMapsUnivlClear = new EventEmitter<void>();
  @Output() filterTargetMapsMerged = new EventEmitter<AutoCompleteCompleteEvent>();
  @Output() targetMapMergedSelect = new EventEmitter<IMappedEntityModel>();
  @Output() targetMapMergedUnselect = new EventEmitter<IMappedEntityModel>();
  @Output() targetMapsMergedClear = new EventEmitter<void>();
  @Output() stepFieldInputChange = new EventEmitter<{ value: string; fieldName: string }>();
  @Output() inputBlur = new EventEmitter<{ value: any; fieldName: string }>();
  @Output() stepFieldImageAttached = new EventEmitter<string>();

  // One editor per entry in testInputFieldList, in the same order (QueryList follows
  // template order), so the description is found by index rather than by a dedicated ref.
  @ViewChildren('stepFieldEditor') stepFieldEditors?: QueryList<RichTextFieldComponent>;

  constructor(
    private jiraHelper: JiraHelper,
    private classicETHelper: ClassicETAPIService,
  ) {}

  get isExternalIntegrationProject(): boolean {
    return this.jiraHelper.isJiraProject() || this.classicETHelper.isClassicETProject();
  }

  public focusTestStepDetails(): void {
    const descriptionIndex = this.testInputFieldList?.findIndex((field) => this.isDescriptionField(field?.fieldName)) ?? -1;
    if (descriptionIndex < 0) {
      return;
    }
    this.stepFieldEditors?.get(descriptionIndex)?.focus();
  }

  public onEnterSubmit(event: KeyboardEvent, value: any, fieldName: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.inputBlur.emit({ value, fieldName });
  }

  public onFieldValueChange(event: any, fieldId: string, fieldName: string): void {
    this.formData[fieldId] = event;
    this.inputBlur.emit({ value: event, fieldName: fieldName || '' });
  }

  public getDisplayLabel(item: IMappedEntityModel | null | undefined): string {
    if (!item) {
      return '';
    }
    const source = item.name || item.path || '';
    return source.length > 20 ? `${source.slice(0, 20)}...` : source;
  }

  public buildLabelTooltip(item: IMappedEntityModel | null | undefined): string {
    if (!item) {
      return '';
    }
    const name = item.name ?? '';
    const path = item.path ?? '';
    if (path) {
      return path;
    }
    return name;
  }

  public getFieldDisplayLabel(field: FieldModel | null | undefined): string {
    const raw = field?.label || field?.name || '';
    return raw.endsWith(':') ? raw.slice(0, -1) : raw;
  }

  public getFieldTooltip(field: FieldModel | null | undefined): string {
    return field?.tooltip || field?.name || '';
  }

  public openAutocompleteDropdown(auto: AutoComplete | null | undefined): void {
    if (!auto || auto.overlayVisible) {
      return;
    }
    auto.show();
  }

  public isDescriptionField(fieldName: string): boolean {
    return (fieldName ?? '').toLowerCase() === TEST_STEP_DESCRIPTION_FIELD;
  }
}
